import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import { CustomerPass } from '../models/customerPass';

export class CustomerPassService extends BaseService<CustomerPass> {
  override model = CustomerPass;

  override modelName = 'customerPass';
  override modelLabel = 'Customer Passes';

  /**
   * Filter customer passes server-side and return the intersection of
   * matching IDs (AND across all active filters).
   *
   * Backend route `POST /customerPass/filter` expects each filter as a
   * `{filter, params}` entry and returns one matched ID list per entry —
   * see `app/Models/Business/Filters/CustomerPassFilter.php`. We transform
   * the flat `{key: params}` object the page hands us (the output of
   * `useFilterController.getFilterParams()`) into that envelope, then
   * intersect the per-filter ID lists so callers receive a single
   * `{data: {ids: number[]}}` shape ready for `listIDs(page)`.
   */
  async filter(filters: Record<string, any>): Promise<ApiResponse<{ ids: number[] }>> {
    const filterArray = Object.entries(filters).map(([filter, params]) => ({
      filter,
      params,
    }));

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/filter`,
      data: { filters: filterArray },
    });

    const response = await this.request(request);
    const results = (response.data?.filters ?? []) as Array<{ ids: number[] }>;

    // No filters applied → empty set (page should fast-path to plain `list()`
    // before reaching us, but guard anyway).
    let ids: number[] = [];
    if (results.length > 0) {
      // AND semantics: keep only ids present in every per-filter result.
      const sets = results.map((r) => new Set(r.ids));
      const first = sets[0]!;
      ids = [...first].filter((id) => sets.every((s) => s.has(id)));
    }

    return { ...response, data: { ids } } as ApiResponse<{ ids: number[] }>;
  }

  async listIDs(
    ids: string[],
    page: number = 1
  ): Promise<ApiListResponse<CustomerPass>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/ids`,
      data: { ids },
      query: { page: page.toString() },
    });

    return await this.requestList<CustomerPass>(request, CustomerPass);
  }

  async email(
    customerPassId: string,
    { fromName, toName, toEmail, body }: { fromName: string; toName: string; toEmail: string; body: string }
  ): Promise<ApiResponse<any>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${customerPassId}/email`,
      data: { fromName, toName, toEmail, body },
    });

    return await this.request(request);
  }
}
